import { forwardRef, useEffect } from 'react';

// material-ui
import {
    Button,
    Dialog,
    FormControl,
    FormHelperText,
    IconButton,
    Grid,
    Stack,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Zoom,
    DialogContent
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import validationSchema from 'utils/schema/PriceSchema';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';

// third-party
import { useFormik } from 'formik';

// assets
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';

const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

const AddPrice = ({ selectedPrice, open, handleClose, venueUuid, setReload, sportsTypes }) => {

    const submitHandler = () => {
        const formikValues = { ...formik.values };
        formikValues.uuid_venue = venueUuid;
        const stInd = sportsTypes.findIndex(x => x.code === formikValues.sports_name);
        formikValues.display_name = sportsTypes[stInd].display_name;
        if (selectedPrice) {
            updatePriceByUuidApi.execute(formikValues, selectedPrice.uuid);
        } else {
            createPriceApi.execute(formikValues);
        }
    };

    const closeDialog = () => {
        formik.resetForm();
        handleClose();
    };

    const createSuccessHandler = () => {
        formik.setSubmitting(true);
        formik.resetForm();
        setReload(r => !r);
        handleClose();
    };

    const createPriceApi = useApi(VenueService.createPrice, createSuccessHandler, true);
    const updatePriceByUuidApi = useApi(VenueService.updatePriceByUuid, createSuccessHandler, true);

    const formik = useFormik({
        initialValues: {
            sports_name: selectedPrice ? selectedPrice.sports_name : '',
            weekday_price: selectedPrice ? parseFloat(selectedPrice.weekday_price) : '',
            weekend_price: selectedPrice ? parseFloat(selectedPrice.weekend_price) : '',
            holiday_price: selectedPrice ? parseFloat(selectedPrice.holiday_price) : ''
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: submitHandler
    });

    useEffect(() => {
        if (selectedPrice) {
            formik.setFieldValue('sports_name', selectedPrice.sports_name);
            formik.setFieldValue('weekday_price', parseFloat(selectedPrice.weekday_price));
            formik.setFieldValue('weekend_price', parseFloat(selectedPrice.weekend_price));
            formik.setFieldValue('holiday_price', parseFloat(selectedPrice.holiday_price));
        }
    }, [selectedPrice]);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={closeDialog}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogContent sx={{ p: 0 }}>
                <MainCard
                    title={selectedPrice ? 'Edit Price Details' : 'Add Price Details'}
                    secondary={
                        <IconButton onClick={closeDialog} size="large" aria-label="Close Edit Price">
                            <HighlightOffTwoToneIcon fontSize="small" />
                        </IconButton>
                    }
                    sx={{ border: 0 }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="medium">
                                    <InputLabel id="sport-type-select-label">Sports Type*</InputLabel>
                                    <Select
                                        labelId="sport-type-select-label"
                                        id="sport-type-select"
                                        label="Sport Type*"
                                        name="sports_name"
                                        value={formik.values.sports_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.sports_name && Boolean(formik.errors.sports_name)}
                                    >
                                        {
                                            sportsTypes.map((sportType, index) => {
                                                return (
                                                    <MenuItem key={index} value={sportType.code}>{sportType.display_name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.sports_name && formik.errors.sports_name && (
                                    <FormHelperText error id="standard-weight-helper-select-sportstype-price">
                                        {formik.errors.sports_name}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="weekday_price"
                                        label="Weekday Price*"
                                        value={formik.values.weekday_price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.weekday_price && Boolean(formik.errors.weekday_price)}
                                    />
                                </FormControl>
                                {formik.touched.weekday_price && formik.errors.weekday_price && (
                                    <FormHelperText error id="standard-weight-helper-text-weekday-price">
                                        {formik.errors.weekday_price}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="weekend_price"
                                        label="Weekend Price*"
                                        value={formik.values.weekend_price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.weekend_price && Boolean(formik.errors.weekend_price)}
                                    />
                                </FormControl>
                                {formik.touched.weekend_price && formik.errors.weekend_price && (
                                    <FormHelperText error id="standard-weight-helper-text-weekend-price">
                                        {formik.errors.weekend_price}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="holiday_price"
                                        label="Holiday Price*"
                                        value={formik.values.holiday_price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.holiday_price && Boolean(formik.errors.holiday_price)}
                                    />
                                </FormControl>
                                {formik.touched.holiday_price && formik.errors.holiday_price && (
                                    <FormHelperText error id="standard-weight-helper-text-holiday-price">
                                        {formik.errors.holiday_price}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button color="error" onClick={closeDialog}>
                                        Cancel
                                    </Button>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={formik.handleSubmit}
                                    >
                                        {selectedPrice ? 'Update' : 'Create'} Price
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </MainCard>
            </DialogContent>
        </Dialog>
    );
};

export default AddPrice;
